import { useState, useMemo } from "react";
import LazyApexChart from "../../../client/components/LazyApexChart";
import { Card, CardContent } from "../../../client/components/ui/card";
import { CardSectionHeader } from "../../../client/components/ui/card-section-header";
import { useDashboardData } from "../DashboardDataContext";
import { chartColors, pnlColor, winRateColor } from "../chart/chartColors";
import ChartViewToggle from "../ChartViewToggle";
import WidgetDataTable from "../WidgetDataTable";
import type { WidgetViewProps } from "../types";

type HoldMode = "count" | "winRate" | "pnl";

function EmptyState({ label }: { label: string }) {
  return (
    <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
      {label}
    </div>
  );
}

// Duration buckets in minutes (upper bound, exclusive) + readable label.
const BUCKETS: { max: number; label: string }[] = [
  { max: 0.25, label: "<15s" },
  { max: 0.75, label: "15-45s" },
  { max: 1, label: "45s-1m" },
  { max: 2, label: "1-2m" },
  { max: 5, label: "2-5m" },
  { max: 10, label: "5-10m" },
  { max: 30, label: "10-30m" },
  { max: 60, label: "30m-1h" },
  { max: 120, label: "1-2h" },
  { max: 240, label: "2-4h" },
  { max: Infinity, label: "4h+" },
];

function bucketIndex(durationMinutes: number): number {
  for (let i = 0; i < BUCKETS.length; i++) {
    if (durationMinutes < BUCKETS[i].max) return i;
  }
  return BUCKETS.length - 1;
}

function fmtShort(v: number): string {
  return Math.abs(v) >= 1000 ? `${(v / 1000).toFixed(1)}k` : v.toFixed(1);
}

export default function HoldDurationWidget({ viewMode = "chart", onSetViewMode }: WidgetViewProps) {
  const { analyticsData, isDark, themeId, t } = useDashboardData();
  const [mode, setMode] = useState<HoldMode>("count");

  const buckets = useMemo(() => {
    if (!analyticsData || analyticsData.holdDurationData.length === 0) return [];
    const acc = BUCKETS.map((b) => ({
      label: b.label,
      count: 0,
      wins: 0,
      pnlSum: 0,
    }));
    for (const p of analyticsData.holdDurationData) {
      const idx = bucketIndex(p.durationMinutes);
      acc[idx].count += 1;
      if (p.isWin) acc[idx].wins += 1;
      acc[idx].pnlSum += p.pnl;
    }
    return acc
      .filter((b) => b.count > 0)
      .map((b) => ({
        label: b.label,
        count: b.count,
        winRate: (b.wins / b.count) * 100,
        avgPnl: b.pnlSum / b.count,
      }));
  }, [analyticsData]);

  const chartOptions = useMemo(() => {
    if (buckets.length === 0) return null;
    const c = chartColors();
    const colors = buckets.map((b) => {
      if (mode === "count") return c.accent;
      if (mode === "winRate") return winRateColor(b.winRate);
      return pnlColor(b.avgPnl);
    });
    return {
      chart: {
        type: "bar" as const,
        toolbar: { show: false },
        background: "transparent",
        foreColor: c.axis,
      },
      plotOptions: {
        bar: { borderRadius: 3, horizontal: true, distributed: true, barHeight: "70%" },
      },
      xaxis: {
        categories: buckets.map((b) => b.label),
        labels: {
          style: { colors: c.axis, fontSize: "10px" },
          formatter: (v: string) => {
            const n = Number(v);
            if (Number.isNaN(n)) return v;
            if (mode === "winRate") return `${n.toFixed(0)}%`;
            if (mode === "pnl") return `${fmtShort(n)}$`;
            return `${n}`;
          },
        },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: {
        labels: { style: { colors: c.axis, fontSize: "10px" } },
      },
      grid: {
        borderColor: c.grid,
        strokeDashArray: 4,
      },
      tooltip: {
        theme: isDark ? "dark" : "light",
        y: {
          formatter: (v: number) => {
            if (mode === "winRate") return `${v.toFixed(0)}%`;
            if (mode === "pnl") return `${v >= 0 ? "+" : ""}${v.toFixed(2)} $`;
            return `${v}`;
          },
        },
      },
      colors,
      legend: { show: false },
      dataLabels: { enabled: false },
    };
  }, [buckets, mode, isDark]);

  const seriesName =
    mode === "count"
      ? t.analytics.numberOfTrades
      : mode === "winRate"
      ? t.analytics.winRate
      : t.analytics.averagePnl;

  const seriesData = useMemo(
    () =>
      buckets.map((b) => {
        if (mode === "count") return b.count;
        if (mode === "winRate") return Math.round(b.winRate * 10) / 10;
        return Math.round(b.avgPnl * 100) / 100;
      }),
    [buckets, mode],
  );

  const tableColumns = [
    { key: "label", label: t.analytics.holdDurationLabel, align: "left" as const },
    { key: "count", label: t.analytics.numberOfTrades, align: "right" as const },
    { key: "winRate", label: t.analytics.winRateLabel, align: "right" as const },
    { key: "avgPnl", label: t.analytics.avgPnlLabel, align: "right" as const },
  ];
  const tableRows = buckets.map((b) => ({
    label: b.label,
    count: b.count,
    winRate: `${b.winRate.toFixed(1)}%`,
    avgPnl: (
      <span className={b.avgPnl >= 0 ? "text-pnlPositive" : "text-pnlNegative"}>
        {b.avgPnl >= 0 ? "+" : ""}
        {b.avgPnl.toFixed(2)}
      </span>
    ),
  }));

  const subToggle = (
    <div className="flex gap-1">
      {(
        [
          ["count", t.analytics.numberOfTrades],
          ["winRate", t.analytics.winRate],
          ["pnl", t.analytics.averagePnl],
        ] as const
      ).map(([m, label]) => (
        <button
          key={m}
          type="button"
          onClick={() => setMode(m)}
          className={`text-xs px-2 py-1 rounded transition-colors ${
            mode === m
              ? "bg-primary text-primary-foreground"
              : "bg-muted/30 text-muted-foreground"
          }`}
        >
          {label}
        </button>
      ))}
    </div>
  );

  const rightContent = (
    <div className="flex items-center gap-2">
      {subToggle}
      <ChartViewToggle value={viewMode} onChange={onSetViewMode} />
    </div>
  );

  if (!analyticsData || analyticsData.holdDurationData.length === 0) {
    return (
      <Card className="h-full flex flex-col">
        <CardSectionHeader
          label={t.analytics.holdDurationTitle}
          isDark={isDark}
          compact
          rightContent={rightContent}
        />
        <CardContent className="flex-1 p-4">
          <EmptyState label={t.analytics.noDataAvailable} />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="h-full flex flex-col">
      <CardSectionHeader
        label={t.analytics.holdDurationTitle}
        isDark={isDark}
        compact
        rightContent={rightContent}
      />
      <CardContent className="flex-1 flex flex-col justify-center px-4 pb-4 pt-1">
        {viewMode === "table" ? (
          <WidgetDataTable columns={tableColumns} rows={tableRows} emptyLabel={t.analytics.noDataAvailable} />
        ) : chartOptions ? (
          <LazyApexChart
            key={`hold-duration-widget-${mode}-${themeId}`}
            options={chartOptions}
            series={[{ name: seriesName, data: seriesData }]}
            type="bar"
            height={300}
          />
        ) : (
          <EmptyState label={t.analytics.noDataAvailable} />
        )}
      </CardContent>
    </Card>
  );
}
